import axios from 'axios'
import type { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth.store'
import { captureApiError } from '@/plugins/sentry'
import { showStatusCodeErrorToast } from '@/utils/errorhandler'
import { isRefreshableRequest } from '@/utils/authHelpers'
import { useToastStore } from '@/stores/toast.store'
import { useErrorPageStore } from '@/stores/error-page.store'

export const STRAPI_URL =
    import.meta.env.VITE_STRAPI_URL || window.appConfig?.apiUrl || 'http://localhost:1337'
const API_BASE_URL = `${STRAPI_URL}/api`

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
})

type ToastControlRequestConfig = AxiosRequestConfig & {
    _retry?: boolean
    suppressGlobalErrorToast?: boolean
}

// WHY: Read the flag at toast time; the 500 may be set while this request was in flight.
function showGlobalErrorToast(error: unknown) {
    if (useErrorPageStore().suppressGlobalErrorToast) return
    showStatusCodeErrorToast(error)
}

async function retryWithFreshToken(
    originalRequest: ToastControlRequestConfig
): Promise<AxiosResponse | undefined> {
    const authStore = useAuthStore()

    if (!originalRequest._retry && isRefreshableRequest(originalRequest)) {
        originalRequest._retry = true

        try {
            await authStore.refreshToken()
            return api(originalRequest)
        } catch (error) {
            showGlobalErrorToast(error)
            await authStore.logoutUser()
        }
    }

    return undefined
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()

    if (authStore.accessToken) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${authStore.accessToken}`
    }

    config.withCredentials = true
    return config
})

api.interceptors.response.use(
    (response) => {
        const toastStore = useToastStore()
        const serverVersion = response.headers['x-app-version']
        const clientVersion = import.meta.env.VITE_APP_VERSION

        if (
            serverVersion &&
            serverVersion !== 'unknown' &&
            clientVersion &&
            serverVersion !== clientVersion
        ) {
            toastStore.showNewVersionToastOnce(serverVersion)
        }
        return response
    },
    async (error: unknown) => {
        if (!axios.isAxiosError(error) || !error.response) {
            captureApiError(error)
            showGlobalErrorToast(error)
            return Promise.reject(error)
        }

        const config = error.config as ToastControlRequestConfig | undefined
        if (config?.suppressGlobalErrorToast) {
            return Promise.reject(error)
        }

        const status = error.response.status

        if (status === 401 && config) {
            const result = await retryWithFreshToken(config)
            if (result) return result
        }
        captureApiError(error)
        showGlobalErrorToast(error)
        return Promise.reject(error)
    }
)

export default api
